import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const CancelConfirmationModal = ({ appointment, services, onConfirm, onClose, getAuthHeaders }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const service = services.find(s => s.id === appointment?.service_id);
  const appointmentDate = appointment?.start_time ? new Date(appointment.start_time).toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }) : '';
  const appointmentTime = appointment?.start_time ? new Date(appointment.start_time).toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit' 
  }) : '';

  const handleConfirmCancel = async () => {
    setLoading(true);
    setError('');
    
    try {
      await axios.delete(`${API_BASE_URL}/appointments/${appointment.id}`, {
        headers: getAuthHeaders()
      });
      
      onConfirm(); // Success - close and refresh
    } catch (err) {
      console.error('Error cancelling appointment:', err);
      setError(err.response?.data?.detail || 'Failed to cancel appointment');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-red-600">Cancel Appointment</h2>
          <p className="text-gray-600 mt-1">Are you sure you want to cancel this appointment?</p>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-md space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-500">Service</p>
              <div className="flex items-center mt-1">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: service?.color_code || '#3B82F6' }}
                ></div>
                <p className="text-gray-900 font-medium">{appointment?.service_name}</p>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Customer</p>
              <p className="text-gray-900">{appointment?.customer_name}</p>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-500">Date & Time</p>
              <p className="text-gray-900">{appointmentDate}</p>
              <p className="text-gray-900">{appointmentTime}</p>
            </div>

            {appointment?.notes && (
              <div>
                <p className="text-sm font-medium text-gray-500">Notes</p>
                <p className="text-gray-900">{appointment.notes}</p>
              </div>
            )}
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md">
            <p className="text-sm text-yellow-800">
              ⚠️ This action cannot be undone. The time slot will become available for other customers.
            </p>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Keep Appointment
          </button>
          <button
            onClick={handleConfirmCancel}
            disabled={loading}
            className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-md transition-colors disabled:bg-red-300"
          >
            {loading ? 'Cancelling...' : 'Confirm Cancellation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelConfirmationModal;
